<template>
  <BasePopup
    ref="popup"
    :title="popupTitle"
    :class="popupClass"
    :closeEvent="closeEvent"
    @hide="step = 1"
  >
    <div v-show="step === 1">
      <p>
        Shortform helps you learn more in less time. Do you value your personal
        growth at more than $1 per hour? If so, keeping Shortform might be worth
        it.
      </p>

      <p class="popup__container">
        <img
          class="popup__container--image"
          src="@/assets.app/images/warren_buffet_disaproves_cancellation.png"
        />
      </p>

      <p>
        We're inspired by Warren Buffett, who said:
        <strong>
          "The best investment you can make is an investment in yourself. The
          more you learn, the more you'll earn."
        </strong>
      </p>
      <form class="cancel-form">
        <div
          class="cancel-form__group--submit cancel-form__buttons cancel-form__buttons--vertical"
        >
          <button
            class="cancel-form__close-btn btns btns--largest btns--font-large btns--full"
            @click="hide(true)"
          >
            Keep my personal growth
          </button>
          <button
            class="cancel-form__btn btns btns--largest btns--font-large btns--white btns--full"
            @click="clickEnd"
          >
            <v-wait for="Loading">
              <template v-slot:waiting>
                Loading
                <button-spinner />
              </template>
              <span>End my personal growth</span>
            </v-wait>
          </button>
        </div>
      </form>
    </div>
    <div v-show="step === 2">
      <div v-if="showAnnualDiscount" class="cancel-form__text">
        <p class="cancel-form__text--large">
          You’ll pay <s>{{ cancelDiscountPricing.yearlyBase }}</s>
          <strong class="cancel-form__text--highlighted">
            <span class="cancel-form__text--small"> </span>
            {{ cancelDiscountPricing.yearlyDiscounted }}
          </strong>
          for the first year <strong>charged today</strong>.
          <span class="cancel-form__text--highlighted">{{
            cancelDiscountPricing.zeroPrice
          }}</span>
          for the first three months, then $16.42/mo for the remaining nine
          months.
        </p>
      </div>

      <div v-else-if="showCancelDiscount" class="cancel-form__text">
        <p class="cancel-form__text--large">
          <s>{{ cancelDiscountPricing.monthlyBase }}</s>
          <strong class="cancel-form__text--highlighted">
            <span class="cancel-form__text--small"> </span>
            {{ cancelDiscountPricing.monthlyDiscounted }}</strong
          >
          for the first month, then $24/month after
        </p>
      </div>

      <p class="cancel-form__books-text">
        We publish 5+ book summaries every week, including these recent hits.
        {{ showCancelDiscount ? '' : 'Click on any to read.' }}
      </p>

      <div class="popup--books books">
        <div
          v-for="(book, index) in books"
          :key="book.id"
          class="popup--books books--container"
          :class="'container__item--' + index"
        >
          <BookCover
            :doc="book"
            class="popup--books books--img"
            @click="openBook(book)"
          />
        </div>
      </div>

      <p
        v-if="showAnnualDiscount"
        class="cancel-form__text cancel-form__text--disclaimer"
      >
        Accepting the discount automatically ends the free trial period.
        Immediately after you select this discount, you will be charged
        {{ cancelDiscountPricing.yearlyDiscounted }} for 1 year of Shortform
        membership.
        <strong
          >You can still cancel your membership before your second
          payment.</strong
        >
      </p>

      <p
        v-else-if="showCancelDiscount"
        class="cancel-form__text cancel-form__text--disclaimer"
      >
        Accepting the discount automatically ends the free trial period.
        <span v-if="showMonthlyDiscountText">
          You will be charged
          {{ cancelDiscountPricing.monthlyDiscounted }} your first month, then
          charged {{ cancelDiscountPricing.monthlyBase }} starting month 2. You
          can change to an annual plan at any time for a prorated cost.</span
        >
        <span v-else>
          <strong>You will be switched to a monthly plan</strong> and charged
          {{ cancelDiscountPricing.monthlyDiscounted }} your first month, then
          charged {{ cancelDiscountPricing.monthlyBase }} starting month 2. You
          can still change back to an annual plan at any time for a prorated
          cost.</span
        >
        <strong>
          You can still cancel your membership before your second
          payment.</strong
        >
      </p>

      <div v-if="error" class="cancel-form__error">
        {{ error }}
      </div>

      <form class="cancel-form">
        <div
          class="popup--books cancel-form__group--submit cancel-form__buttons"
        >
          <button
            class="cancel-form__close-btn btns btns--largest btns--font-large btns--full"
            @click="clickKeep"
          >
            <v-wait for="Applying Discount">
              <template v-slot:waiting>
                Applying Discount
                <button-spinner />
              </template>
              <span>{{ ctaText }}</span>
            </v-wait>
          </button>
          <button
            v-wait:disabled="'Canceling'"
            class="cancel-form__btn btns btns--largest btns--font-large btns--white btns--full"
            @click="cancelSubscription"
          >
            <v-wait for="Canceling">
              <template v-slot:waiting>
                Ending
                <button-spinner />
              </template>
              <span>End my membership</span>
            </v-wait>
          </button>
        </div>
      </form>
    </div>
  </BasePopup>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { useRouter } from 'vue-router'

import { BillingView } from '@/models/billing'

import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import BasePopup from '@/components/ui/BasePopup.vue'
import BookCover from '@/app/components/book/card/BookCover.vue'

import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { Book } from '@/models/interfaces'
import { Mixpanel } from '@/analytics/mixpanel'

const props = defineProps<{
  billing: BillingView
}>()

const emit = defineEmits(['canceled'])
const router = useRouter()
const wait = useWait()

const popup = ref<InstanceType<typeof BasePopup> | null>(null)

// For two-step cancellation flow, starting step is 1
const step = ref(1)
const books = ref<Book[]>([])
const cancelDiscountRegistered = ref<boolean | null>(null)
const error = ref('')

onMounted(async () => {
  books.value = await backend.getLatestPopularBooks()
})

const cancelDiscountPricing = ref({
  monthlyBase: '$24/month',
  yearlyBase: '$197',
  zeroPrice: '$0.00',
  yearlyDiscounted: '$147.75',
  monthlyDiscounted: '$12',
})

/*
 * Set the popup title depending on the current step.
 *
 * Step 1: The first step in the two-step cancellation flow.
 * Step 2: The second step in the two-step cancellation flow.
 */
const popupTitle = computed(() => {
  switch (step.value) {
    case 1:
      return 'Are you sure you want to end your subscription?'
    case 2:
      if (!showCancelDiscount.value) {
        return 'Before you go, check out our recent hit summaries.'
      }

      if (showAnnualDiscount.value) {
        return 'Still not sure? Pay now and get the <u>first 3 months free!</u>'
      }

      return 'Still not sure? Try 1 month for <u>50% off</u>!'
  }
  return 'Cancel your subscirption?'
})

/*
 * Set the popup class depending on the current step.
 *
 * For the 1st step we use 'popup--simple',
 * for the 2nd step we need to use a different class
 * because the style of the popup is different.
 */
const popupClass = computed(() => {
  if (step.value === 2 && showCancelDiscount.value) {
    return 'popup--books popup--15449'
  }
  if (step.value === 2) {
    return 'popup--books'
  }
  return 'popup--simple'
})

async function clickKeep(event: Event): Promise<void> {
  event.preventDefault()

  if (showCancelDiscount.value) {
    try {
      event.preventDefault()
      error.value = ''
      wait.start('Applying Discount')
      await props.billing.applyCancelDiscount()
      hide()
      // We need to reload the page as we need to read the local storage again
      window.location.reload()
      wait.end('Applying Discount')
    } catch (err) {
      wait.end('Applying Discount')
      Sentry.captureException(err)
      error.value = 'Failed to apply discount'
    }
  } else {
    hide()
  }
}

function show(): void {
  popup.value?.show()
}

function closeEvent(): void {
  Mixpanel.trackCancelSubscriptionStep(step.value, 'Close Window')
}

function hide(sendEvent: boolean = true): void {
  if (sendEvent) {
    Mixpanel.trackCancelSubscriptionStep(step.value, 'Keep Subscription')
  }
  popup.value?.hide(false)
  step.value = 1
}

/**
 * Returns true if the cancel discount is registered and the user is on a trial.
 *
 * @returns {boolean} True if the cancel discount is registered and the user is on a trial, false otherwise.
 */
const showCancelDiscount = computed(() => {
  return cancelDiscountRegistered.value === true && props.billing.is_trial
})

/**
 * Returns true if the user is on a monthly plan.
 *
 * @returns {boolean} True if the user is on a monthly plan, false otherwise.
 */
const showMonthlyDiscountText = computed(() => {
  return props.billing.plan_id === 'plan_monthly'
})

/**
 * Returns true if the cancel discount is registered and the user is on an annual plan.
 *
 * @returns {boolean} True if the cancel discount is registered and the user is on an annual plan, false otherwise.
 */
const showAnnualDiscount = computed(() => {
  return (
    cancelDiscountRegistered.value === true &&
    props.billing.is_trial &&
    props.billing.plan_id === 'plan_annual'
  )
})

/**
 * Returns the text for the CTA button.
 *
 * @returns {string} The text for the CTA button.
 */
const ctaText = computed(() => {
  if (!(cancelDiscountRegistered.value === true) || !props.billing.is_trial) {
    return 'Keep my membership'
  }

  if (showAnnualDiscount.value) {
    return 'Pay now, get the first 3 months free'
  }

  return 'Get 50% off'
})

async function clickEnd(event: Event): Promise<void> {
  wait.start('Loading')
  event.preventDefault()
  Mixpanel.trackCancelSubscriptionStep(step.value, 'End Subscription')

  cancelDiscountRegistered.value = await backend.registerCancelDiscount()
  step.value += 1
  wait.end('Loading')
}

function openBook(book: any): void {
  Mixpanel.trackCancelSubscriptionStep(step.value, 'Open Book', {
    book_title: book.title,
  })

  if (cancelDiscountRegistered.value === true) {
    return
  }

  router.push({
    name: 'book.page',
    params: {
      url_slug: book.url_slug,
      position: book.position,
    },
  })
}

async function cancelSubscription(event: Event): Promise<void> {
  try {
    event.preventDefault()
    wait.start('Canceling')
    await props.billing.subscribeTo(props.billing.freePlan)
    emit('canceled')
    wait.end('Canceling')
  } catch (error) {
    wait.end('Canceling')
    Sentry.captureException(error)
    alert('Failed to cancel the subscription')
  }
  Mixpanel.trackCancelSubscriptionStep(step.value, 'End Membership')
  hide(false)
}

defineExpose({
  show,
  hide,
})
</script>

<style lang="scss" scoped>
.cancel-form {
  &__error {
    color: $c-red;
    margin-bottom: 1rem;
    text-align: center;
  }
  &__close-btn {
    min-width: fit-content;
    margin: 0px;
  }
  &__buttons {
    display: flex;
    flex-direction: row;
    gap: 16px;
    margin-left: 0px !important;
    @media only screen and (max-width: $w-mobile-s) {
      flex-direction: column;
      justify-content: space-between;
      display: flex !important;
    }

    &--vertical {
      flex-direction: column;
    }
  }

  &__text {
    &--highlighted {
      color: $c-red;
    }

    &--small {
      margin-bottom: 0px !important;
    }
  }

  &__text--large {
    font-size: 18px;
    margin-bottom: 32px;
  }
}

.popup--15449 {
  :deep(.popup__title-box) {
    padding-bottom: 0;
    border: 0;
    margin-bottom: 0;
  }

  :deep(.popup__title) {
    margin-bottom: 12px;
  }

  .books {
    pointer-events: none;

    @media only screen and (max-width: $w-tablet-s) {
      margin-left: -12px;
      overflow: hidden;
      margin-top: 16px;
      display: flex;
      flex-direction: row;
      gap: 4px;
    }

    @media only screen and (max-width: $w-mobile-s) {
      gap: 0;
    }
  }

  .cancel-form {
    &__books-text {
      margin-bottom: 0;
    }

    &__text--disclaimer {
      margin-top: 16px;
    }
  }
}
</style>
